/**
 * Phase 3–4: public cancellation confirmation (no auth).
 */

import { CANCELLATION_CONFIRM_PUBLIC_HTTP } from '../constants/accountCancellationWorkflow';
import { getApiUrl } from '../utils/backendUrl';
import Logger from './logger';

export type CancellationTokenPublicState =
  | 'pending'
  | 'completed'
  | 'expired'
  | 'invalid'
  | 'not_available';

export async function fetchCancellationConfirmTokenState(
  token: string
): Promise<CancellationTokenPublicState> {
  const t = token.trim();
  if (!t) {
    return 'invalid';
  }
  try {
    const base = getApiUrl();
    const path = CANCELLATION_CONFIRM_PUBLIC_HTTP.TOKEN_STATUS_PATH;
    const url = `${base}${path}?token=${encodeURIComponent(t)}`;
    const r = await fetch(url);
    const data = (await r.json().catch(() => ({}))) as { state?: string };
    const s = data.state;
    if (
      s === 'pending' ||
      s === 'completed' ||
      s === 'expired' ||
      s === 'invalid' ||
      s === 'not_available'
    ) {
      return s;
    }
    return 'invalid';
  } catch (e) {
    Logger.error(
      'fetchCancellationConfirmTokenState failed',
      e instanceof Error ? e : new Error(String(e))
    );
    return 'invalid';
  }
}

export type PostCancellationCompleteResult =
  | { ok: true; state: 'completed' | 'already_completed' }
  | { ok: false; code?: string; message: string };

export async function postCancellationConfirmComplete(
  token: string
): Promise<PostCancellationCompleteResult> {
  const t = token.trim();
  if (!t) {
    return { ok: false, code: 'MISSING_TOKEN', message: 'Missing token' };
  }
  try {
    const base = getApiUrl();
    const path = CANCELLATION_CONFIRM_PUBLIC_HTTP.COMPLETE_PATH;
    const r = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t }),
    });
    const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    const ok = data.ok === true;
    const state = data.state;
    if (ok && (state === 'completed' || state === 'already_completed')) {
      return { ok: true, state };
    }
    const message =
      (typeof data.message === 'string' && data.message) ||
      (typeof data.error === 'string' && data.error) ||
      'Request failed';
    const code = typeof data.code === 'string' ? data.code : undefined;
    return { ok: false, code, message };
  } catch (e) {
    Logger.error(
      'postCancellationConfirmComplete failed',
      e instanceof Error ? e : new Error(String(e))
    );
    return { ok: false, code: 'NETWORK', message: 'Network error' };
  }
}
